import React, { useRef, useEffect, useState } from 'react';
import AdyenCheckout from '@adyen/adyen-web';
import styled from 'styled-components';
import logoVisa from './logo-visa.jpg';
import logoMastercard from './logo-mastercard.png';
import logoVipps from './logo-vipps.png';
import logoMobilePay from './logo-mobile-pay.png';
import { XButton } from '../../../../x/button/XButton';
import clsx from 'clsx';
import { useToggle } from '../../../../../hooks/toggle';

const Wrapper = styled.div`
    position: relative;
    background: white;
    border-radius: 40px;
    transform: translateZ(0);
    overflow: hidden;
    min-height: var(--x-size-2);

    .adyen-checkout__button,
    ._XButton {
        height: 48px !important;
        width: 100%;
        border-radius: 40px;
    }

    &.applepay,
    &.paywithgoogle,
    &.vipps {
        height: var(--x-size-2);

        button {
            width: 100%;
            height: 100%;
        }
    }

    &.applepay {
        background-color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 48px;
        border-radius: 40px;

        > .container {
            height: 48px;
            flex: 1;
        }

        button {
            -webkit-appearance: -apple-pay-button;
            -apple-pay-button-style: white;
        }
    }

    &.paywithgoogle button {
        overflow: hidden;
    }

    &.mobilepay,
    &.vipps {
        button {
            background-color: white;
        }

        .adyen-checkout__button__content:before {
            background-size: cover;
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 1;
        }

        .adyen-checkout__button__text {
            display: none;
        }
    }

    &.mobilepay {
        .adyen-checkout__button__content:before {
            height: 32px;
            width: 100px;
            background-image: url(${logoMobilePay});
        }
    }

    &.vipps {
        .adyen-checkout__button__content:before {
            height: 24px;
            width: 96px;
            background-image: url(${logoVipps});
        }
    }

    &.scheme {
        background-color: white;
        border-radius: 40px;

        .adyen-checkout__button__icon {
            display: none;
        }

        .adyen-checkout__button__text {
            font-weight: bold;
        }

        ._XButton {
            display: flex;
            justify-content: center;
            width: 100%;
            transition: border-radius 300ms;
        }

        > .container {
            display: none;
            background-color: white;
            border-bottom-left-radius: var(--x-border-radius);
            border-bottom-right-radius: var(--x-border-radius);

            button {
                background-color: var(--x-color-body);
                font-size: 18px;
                text-transform: uppercase;
                letter-spacing: 1px;
            }

            .adyen-checkout__label__text {
                font-weight: bold;
            }

            .adyen-checkout__button__text {
                text-overflow: initial;
            }
        }

        &.isSchemeVisible {
            padding: var(--space4);
            padding-top: var(--space2);
            border-radius: 16px;

            .container {
                display: block;
                margin-top: var(--space4);
            }
        }

        ._XButton {
            background-color: white;
            color: black;
            text-transform: uppercase;
            letter-spacing: 1px;

            .logo-container {
                display: flex;
                align-items: center;
            }

            .logo-visa {
                background-image: url(${logoVisa});
                background-size: cover;
                height: 32px;
                width: 56px;
            }

            .logo-mastercard {
                background-image: url(${logoMastercard});
                background-size: cover;
                height: 48px;
                width: 68px;
            }
        }
    }
`;

interface Props {
    checkout: AdyenCheckout;
    type: 'paywithgoogle' | 'applepay' | 'vipps' | 'scheme' | string;
    configuration?: any;
    onSuccess: any;
}

export function ProviderPayAdyenProvider({ checkout, type, configuration }: Props) {
    const [providerElementId] = useState(`provider-${Math.random()}`);
    const ref = useRef<any>();
    const [error, setIsError] = useState(false);
    const [isSchemeVisible, toggleIsSchemeVisible] = useToggle();
    const isScheme = type === 'scheme';

    useEffect(() => {
        if (ref?.current) {
            const element = ref.current;

            try {
                const dropin = checkout.create(type, configuration);

                if (dropin.isAvailable) {
                    dropin
                        .isAvailable()
                        .then(() => {
                            dropin.mount(element);
                        })
                        .catch(() => {
                            setIsError(true);
                        });
                } else {
                    dropin.mount(element);
                }
            } catch (error) {
                console.error(error);
                setIsError(true);
            }
        }
    }, [ref]);

    if (error) {
        return null;
    }

    return (
        <>
            <Wrapper className={clsx(type, { isSchemeVisible })}>
                {isScheme && (
                    <XButton onClick={toggleIsSchemeVisible}>
                        <div className="logo-container">
                            <div className="logo-visa" />
                            <div className="logo-mastercard" />
                        </div>
                    </XButton>
                )}

                <div ref={ref} id={providerElementId} className="container" />
            </Wrapper>

            {/*{Environment.IS_DEV && type === 'scheme' && (*/}
            {/*    <UiGrid>*/}
            {/*        <input*/}
            {/*            id="card_id1"*/}
            {/*            value="4988 4388 4388 4305"*/}
            {/*            onClick={() => {*/}
            {/*                const copyText = document.querySelector('#card_id1') as any;*/}
            {/*                copyText.select();*/}
            {/*                document.execCommand('copy');*/}
            {/*            }}*/}
            {/*            onChange={() => {}}*/}
            {/*        />*/}

            {/*        <UiFlex flexChildren gap={1}>*/}
            {/*            <input*/}
            {/*                id="card_id2"*/}
            {/*                value="03/30"*/}
            {/*                onClick={() => {*/}
            {/*                    const copyText = document.querySelector('#card_id2') as any;*/}
            {/*                    copyText.select();*/}
            {/*                    document.execCommand('copy');*/}
            {/*                }}*/}
            {/*                onChange={() => {}}*/}
            {/*            />*/}

            {/*            <input*/}
            {/*                id="card_id3"*/}
            {/*                value="737"*/}
            {/*                onClick={() => {*/}
            {/*                    const copyText = document.querySelector('#card_id3') as any;*/}
            {/*                    copyText.select();*/}
            {/*                    document.execCommand('copy');*/}
            {/*                }}*/}
            {/*                onChange={() => {}}*/}
            {/*            />*/}
            {/*        </UiFlex>*/}
            {/*    </UiGrid>*/}
            {/*)}*/}
        </>
    );
}
